import { ProgressSpinner } from '@pledge-earth/product-language';
import * as Sentry from '@sentry/react';
import type { ComponentType } from 'react';
import { Suspense, lazy } from 'react';
import type { UIMatch } from 'react-router';
import { Navigate, Outlet, createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { AuthLayout } from './layouts/Auth/AuthLayout';
import { BaseLayout } from './layouts/BaseLayout';
import { MainLayout } from './layouts/Main/MainLayout';
import { getIntl } from './locales/intl';

function lazyComponent<TComponent extends ComponentType<any>, TModule>(
  module: Promise<TModule>,
  componentSelector: (module: TModule) => TComponent,
) {
  return lazy(() =>
    module.then((module) => ({ default: componentSelector(module) })),
  );
}

const Dashboard = lazyComponent(
  import('./pages/dashboard/Dashboard'),
  (module) => module.Dashboard,
);

const Clients = lazyComponent(
  import('./pages/clients/Clients'),
  (module) => module.Clients,
);

const Client = lazyComponent(
  import('./pages/clients/client/Client'),
  (module) => module.Client,
);

const Tools = lazyComponent(
  import('./pages/tools/tools'),
  (module) => module.Tools,
);

const AuthLogin = lazyComponent(
  import('./pages/auth/login/SystemLogin'),
  (module) => module.SystemLogin,
);
const Auth404 = lazyComponent(
  import('./pages/auth/404/System404'),
  (module) => module.System404,
);
const Auth500 = lazyComponent(
  import('./pages/auth/500/System500'),
  (module) => module.System500,
);
const DataCollectionConfigureSupplierEntitlements = lazyComponent(
  import('./pages/dataCollection/ConfigureSupplierEntitlements'),
  (module) => module.ConfigureSupplierEntitlements,
);
const DataCollectionLinkImport = lazyComponent(
  import('./pages/dataCollection/LinkImport'),
  (module) => module.LinkImport,
);
const DataCollectionSubmitRequestData = lazyComponent(
  import('./pages/dataCollection/SubmitRequestData'),
  (module) => module.SubmitRequestData,
);

const sentryCreateBrowserRouter =
  process.env.NODE_ENV === 'production'
    ? Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)
    : createBrowserRouter;

const { formatMessage } = getIntl();

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: (
      <BaseLayout>
        <MainLayout>
          <Suspense
            fallback={
              <div className="flex h-80 w-full items-center justify-center">
                <ProgressSpinner className="size-16" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </MainLayout>
      </BaseLayout>
    ),
    children: [
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'clients',
        handle: { crumb: () => formatMessage({ id: 'clients.title' }) },
        children: [
          {
            index: true,
            element: <Clients />,
          },
          {
            path: ':clientId',
            handle: { crumb: ({ params }: UIMatch) => params.clientId },
            children: [
              {
                index: true,
                element: <Client />,
              },
            ],
          },
        ],
      },
      {
        path: 'data-collection/configure-supplier-entitlements',
        element: <DataCollectionConfigureSupplierEntitlements />,
      },
      {
        path: 'data-collection/link-import',
        element: <DataCollectionLinkImport />,
      },
      {
        path: 'data-collection/submit-request-data',
        element: <DataCollectionSubmitRequestData />,
      },
      {
        path: 'tools',
        element: <Tools />,
      },
    ],
  },
  {
    path: '/auth',
    element: (
      <BaseLayout isAuthLayout={true}>
        <AuthLayout>
          <Suspense>
            <Outlet />
          </Suspense>
        </AuthLayout>
      </BaseLayout>
    ),
    children: [
      {
        path: 'login',
        element: <AuthLogin />,
      },
      {
        path: '404',
        element: <Auth404 />,
      },
      {
        path: '500',
        element: <Auth500 />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/auth/404" replace={true} />,
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
